import React, {useState} from 'react';
import {useLocation} from "react-router-dom";
import ReactModal from "react-modal";
import {useForm} from 'react-hook-form';
import useLoading from '../../../../hooks/useLoading';
import { roomSalesmanApi } from '../../../../apis/admin/room/roomSalesmanApi';
import {ApiClientError} from "../../../../apis/errors";
import useAppRouter from "../../../../hooks/useAppRouter";
import {ValidationErrorMessages} from "../../common/ValidationErrorMessages";
import {FormValidationType, makeRegisterOptions} from "../../../../libs/reactHookForm/makeRegisterOptions";
import {logError} from "../../../../libs/utils/logError";

interface CreateSalesmanModalProps {
  onSubmitted: (isChange: boolean) => Promise<void>;
}

interface FormValuesOfCreateUser {
  userName: string;
  companyName: string;
  email: string;
  department: string;
  employeeNumber: string;
}


export const CreateSalesmanModal = (props: CreateSalesmanModalProps) => {
  const { onSubmitted } = props;

  const withLoading = useLoading();
  const location = useLocation();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    reValidateMode: 'onSubmit',
  });

  const [ apiClientError, setApiClientError ] = useState<ApiClientError | null>(null);
  const { goto } = useAppRouter();

  const handleUserInfoChange = async (data: FormValuesOfCreateUser) => {
    withLoading(async () => {
      setApiClientError(null);

      try {
        await roomSalesmanApi.create(data, {});

        await onSubmitted(true)

      } catch (e) {
        await logError(
          location.pathname,
          '営業ユーザーの作成に失敗しました。',
          e
        );

        if (e instanceof ApiClientError) {
          setApiClientError(e);
          return;
        }

        goto('internalServerError');
      }

    });
  };

  return (
    <>
      <ReactModal
        isOpen
        ariaHideApp={false}
        onRequestClose={() => onSubmitted(false)}
        overlayClassName="overlay"
        shouldCloseOnOverlayClick
        className="new-form-modal"
      >
        <section className="modal-background">
          <div className="form-modal-container">
            <div className="form-modal-title">
              <h1 className="font-semibold">営業担当追加</h1>
              <button
                type="button"
                className="modal-close-btn"
                onClick={() => onSubmitted(false)}
              >
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit(handleUserInfoChange)} id="createUserForm">
              <div className="form-modal-content overflow-y-auto" style={{ flexDirection: 'unset', height: '30rem' }}>
                <div className="grid grid-cols-1 w-full">
                {(apiClientError?.message || Object.keys(errors).length > 0) && (
                  <>
                    <div className="text-danger text-xs mb-4">
                      { apiClientError?.message }
                      { (Object.keys(errors).length > 0) && '入力エラーがあります' }
                    </div>
                  </>
                )}

                  <label className="block" htmlFor="userName">
                    <div className="text-sm font-semibold text-primary mb-1">
                      <span>お名前</span>
                      <span className="ml-2 required">必須</span>
                    </div>
                    <input
                      /* eslint-disable-next-line react/jsx-props-no-spreading */
                      {...register('userName',
                        makeRegisterOptions('お名前', [
                          { type: FormValidationType.REQUIRED },
                          { type: FormValidationType.MAX_STRING_LENGTH, value: 100 },
                        ])
                      )}
                      id="userName"
                      className="text-field"
                      type="text"
                      maxLength={100}
                    />
                    <div className="mt-1 text-danger text-xs">
                      {errors.userName?.message}
                    </div>
                    <ValidationErrorMessages
                      {...{
                        fieldErrors: apiClientError?.errors,
                        field: 'userName',
                      }}
                    />
                  </label>

                  <label className="block mt-4" htmlFor="email">
                    <div className="text-sm font-semibold text-primary mb-1">
                      <span>メールアドレス</span>
                      <span className="ml-2 required">必須</span>
                    </div>
                    <input
                      /* eslint-disable-next-line react/jsx-props-no-spreading */
                      {...register('email',
                        makeRegisterOptions('メールアドレス', [
                          { type: FormValidationType.REQUIRED },
                          { type: FormValidationType.MAX_STRING_LENGTH, value: 86 },
                          { type: FormValidationType.EMAIL, typeName: 'email' },
                        ])
                      )}
                      id="email"
                      className="text-field"
                      type="text"
                      maxLength={86}
                    />
                    <div className="mt-1 text-danger text-xs">
                      {errors.email?.message}
                    </div>
                    <ValidationErrorMessages
                      {...{
                        fieldErrors: apiClientError?.errors,
                        field: 'email',
                      }}
                    />
                  </label>


                  <label className="block mt-4" htmlFor="companyName">
                    <div className="text-sm font-semibold text-primary mb-1">
                      <span>会社名</span>
                    </div>
                    <input
                      /* eslint-disable-next-line react/jsx-props-no-spreading */
                      {...register('companyName',
                        makeRegisterOptions('会社名', [
                          { type: FormValidationType.MAX_STRING_LENGTH, value: 100 },
                        ])
                      )}
                      className="text-field"
                      id="companyName"
                      type="text"
                      maxLength={100}
                    />
                    <div className="mt-1 text-danger text-xs">
                      {errors.companyName?.message}
                    </div>
                    <ValidationErrorMessages
                      {...{
                        fieldErrors: apiClientError?.errors,
                        field: 'companyName',
                      }}
                    />
                  </label>

                  <label className="block mt-4" htmlFor="department">
                    <div className="text-sm font-semibold text-primary mb-1">
                      <span>部署名</span>
                    </div>
                    <input
                      /* eslint-disable-next-line react/jsx-props-no-spreading */
                      {...register('department',
                        makeRegisterOptions('部署名', [
                          { type: FormValidationType.MAX_STRING_LENGTH, value: 100 },
                        ])
                      )}
                      className="text-field"
                      id="department"
                      type="text"
                      maxLength={100}
                    />
                    <div className="mt-1 text-danger text-xs">
                      {errors.department?.message}
                    </div>
                    <ValidationErrorMessages
                      {...{
                        fieldErrors: apiClientError?.errors,
                        field: 'department',
                      }}
                    />
                  </label>
                </div>
              </div>
            </form>


            <div className="form-modal-footer-container">
              <div className="form-modal-footer">
                <button
                  className="btn-sub"
                  type="button"
                  onClick={() => onSubmitted(false)}
                  >
                  キャンセル
                </button>

                <button
                  className="float-right btn-primary"
                  type="submit"
                  form="createUserForm"
                  >
                  追 加
                </button>
              </div>
            </div>
          </div>
        </section>
      </ReactModal>
    </>
  );
};
